import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { useTranslation } from 'react-i18next'
import { object, SchemaOf, string } from 'yup'
import { Errors } from 'config'
import { useMapTwoFactorError } from 'shared/hooks/use-map-two-factor-error'
import type { FormProps } from '../Form'

export const TWO_FACTOR_CODE_LENGTH = 6

export enum TwoFactorCodeFormFields {
  Code = 'code',
}

export interface TwoFactorCodeFormValues {
  [TwoFactorCodeFormFields.Code]: string
}

const INVALID_CODE_ERRORS: string[] = [
  Errors.InvalidVerificationCode,
  Errors.MissingVerificationCode,
]

// An invalid code is shown on the field itself, any other error above the form
export const useTwoFactorCodeForm = (
  onSubmit: (code: string) => void | Promise<void>,
  error: string | null
) => {
  const { t } = useTranslation()
  const { mapError } = useMapTwoFactorError()
  const schema: SchemaOf<TwoFactorCodeFormValues> = object().shape({
    [TwoFactorCodeFormFields.Code]: string()
      .required(t('validation.required'))
      .matches(
        new RegExp(`^\\d{${TWO_FACTOR_CODE_LENGTH}}$`),
        t('validation.twoFactorCode', { length: TWO_FACTOR_CODE_LENGTH })
      ),
  })
  const methods = useForm<TwoFactorCodeFormValues>({
    defaultValues: { [TwoFactorCodeFormFields.Code]: '' },
    resolver: yupResolver(schema),
  })
  const isInvalidCodeError = !!error && INVALID_CODE_ERRORS.includes(error)

  useEffect(() => {
    if (!error || !isInvalidCodeError) return
    methods.setError(TwoFactorCodeFormFields.Code, {
      message: mapError(error),
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error])

  const formProps: FormProps<TwoFactorCodeFormValues> = {
    ...methods,
    onSubmit: values => onSubmit(values[TwoFactorCodeFormFields.Code]),
  }

  return {
    formProps,
    formError: error && !isInvalidCodeError ? mapError(error) : null,
  }
}
